import { AntDesign, EvilIcons, Ionicons } from '@expo/vector-icons'
import React, { useEffect, useRef } from 'react'
import { View, StyleSheet, TouchableOpacity, Dimensions, Platform } from 'react-native'
import Colors from '../../../../constants/Colors'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { largeTextSize, titleTextSize } from '../../../../constants/GlobalStyle'
import { logCompleteCheckoutAnalytics } from '../../../../firebase/analytics'
import { add, sub } from 'date-fns'
import { parsePrepTime } from '../../../../helpers/prepTimeFunctions'
import { Text } from '../../../../components/Themed'
import crashlytics from '../../../../components/Crashlytics/crashlyticsLog'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import { prepText } from '../../../../helpers/prepText'
import { resetReceiptMethods, setCheckoutState } from '../../../../state/Slices/checkoutSlice'
import { cloudFunction } from '../../../../firebase/functions'
import StarPrintReceipt from '../../../../components/Kiosk/Receipts/Star/StarPrintReceipt.native'
import { setDataState } from '../../../../state/Slices/dataSlice'
import { resetCartPrepState } from '../../../../state/Slices/persistedSlice'
import { useInitializeOrder } from '../../../../hooks/useInitializeOrder'
import { useNavigation } from '@react-navigation/native'
import { useEmptyCart } from '../../../../hooks/useEmptyCart'
/**
 * Screen to display after order is successfully placed
 */
const OrderConfirmed = ({ setIsOrderConfirmedModalVisible }) => {
	const {
		selectedMethods,
		orderId,
		analyticsItems,
		prices,
		posLoyaltyPoints,
		posLoyaltyPointsAccrued,
		orderData,
		orderNumber,
		modalTimestamps,
		products,
	} = useAppSelector(state => state.checkout)

	const dispatch = useAppDispatch()

	const { posLoyaltyEnabled, deliveryEnabled, delivery, localTimezone } = useAppSelector(
		state => state.settings
	)
	const { loadedData, webAppURL } = useAppSelector(state => state.data)
	const { userLocation } = useAppSelector(state => state.user)
	const {
		cart,
		cartPrep,
		isTestMode,
		isDelivery,
		selectedPickupPoint,
		contactlessDropoff,
		receiptPrinter,
	} = useAppSelector(state => state.persisted)
	const navigation = useNavigation<any>()

	const initializeOrder = useInitializeOrder()
	const emptyCart = useEmptyCart()

	const handleDone = () => {
		setIsOrderConfirmedModalVisible(false)
		initializeOrder()
		emptyCart()

		if (Platform.OS === 'web') {
			navigation.replace('HomeStack', { screen: 'RestaurantSelect' })
		} else {
			navigation.reset({
				index: 0,
				routes: [{ name: 'HomeStack' }],
			})
		}

		dispatch(resetReceiptMethods())
	}

	const descriptor = global.pos ? 'THE' : 'YOUR'
	const pronoun = global.pos ? 'THEY' : 'YOU'

	const showLoyalty =
		posLoyaltyPoints > 0 && posLoyaltyEnabled && selectedMethods.phoneString !== ''

	let orderText = ''

	if (selectedMethods.textMessage && selectedMethods.email && selectedMethods.print) {
		orderText = `PLEASE COLLECT ${descriptor} RECEIPT FROM THE PRINTER. ${descriptor} RECEIPT WILL BE SENT TO ${descriptor} PHONE AND EMAIL ADDRESS.`
	} else if (selectedMethods.textMessage && selectedMethods.email) {
		orderText = `${descriptor} RECEIPT WILL BE SENT TO ${descriptor} PHONE AND EMAIL ADDRESS.`
	} else if (selectedMethods.email && selectedMethods.print) {
		orderText = `PLEASE COLLECT ${descriptor} RECEIPT FROM THE PRINTER. ${descriptor} RECEIPT WILL BE SENT TO ${descriptor} EMAIL ADDRESS.`
	} else if (selectedMethods.textMessage && !selectedMethods.print) {
		orderText = `${descriptor} RECEIPT WILL BE SENT TO ${descriptor} PHONE.`
	} else if (selectedMethods.textMessage && selectedMethods.print) {
		orderText = `PLEASE COLLECT ${descriptor} RECEIPT FROM THE PRINTER. ${descriptor} RECEIPT WILL BE SENT TO ${descriptor} PHONE.`
	} else if (selectedMethods.email && !selectedMethods.print) {
		orderText = `${descriptor} RECEIPT WILL BE SENT TO ${descriptor} EMAIL ADDRESS.`
	} else if (selectedMethods.print) {
		orderText = `PLEASE COLLECT ${descriptor} RECEIPT FROM THE PRINTER.`
	}

	const pickupTextData = prepText(cartPrep, isDelivery, true)

	const functions = getFunctions()

	const hasRun = useRef(false)

	useEffect(() => {
		if (!orderId) {
			setIsOrderConfirmedModalVisible(false)
			return
		}

		console.log(
			`[Checkout.Modals.PlaceOrder.OrderConfirmed] Beginning to send receipts for order ${orderId}.`
		)
		const sendEmailReceipt = async () => {
			try {
				console.log('[Checkout.Modals.PlaceOrder.OrderConfirmed] Sending email receipt.')
				crashlytics().log('SENDING EMAIL RECEIPT')
				const response = await cloudFunction('v2_sendEmailReceiptTest', {
					userEmail: selectedMethods.emailString,
					receiptLink: webAppURL + '/receipt?receiptId=' + orderId,
					orgLogo: loadedData[global.org].logoDark ?? loadedData[global.org].logo,
					emailLogo: loadedData[global.org].emailLogo || null,
					orgName: loadedData[global.org].name,
					orgColor: global.orgColor2,
				})
				console.log('[Checkout.Modals.PlaceOrder.OrderConfirmed] Email receipt sent.')
				crashlytics().log('EMAIL RECEIPT SENT.')

				console.log(response.data)
			} catch (error) {
				console.error(
					'[Checkout.Modals.PlaceOrder.OrderConfirmed] Error calling cloud function',
					error
				)
				crashlytics().log('Error calling cloud function' + error)
			}
		}
		const sendTextReceipt = async () => {
			try {
				console.log('[Checkout.Modals.PlaceOrder.OrderConfirmed] Sending text receipt.')
				crashlytics().log('SENDING TEXT RECEIPT')
				const response = await cloudFunction('v2_sendTextReceipt', {
					userPhone: selectedMethods.phoneString,
					receiptLink: webAppURL + '/receipt?receiptId=' + orderId,
					organizationName: loadedData[global.org].name,
				})
				console.log('[Checkout.Modals.PlaceOrder.OrderConfirmed] Text receipt sent.')
				crashlytics().log('TEXT RECEIPT SENT.')

				console.log(response.data)
			} catch (error) {
				console.error(
					'[Checkout.Modals.PlaceOrder.OrderConfirmed] Error calling cloud function',
					error
				)
				crashlytics().log('Error calling cloud function' + error)
			}
		}
		const addLoyaltyPointsToUser = async () => {
			try {
				const updateUserPoints = httpsCallable(functions, 'updateUserPoints')
				console.log('UPDATING USER POINTS')
				crashlytics().log('UPDATING USER POINTS')
				const response = await updateUserPoints({
					orgId: global.org,
					uid: orderData.userId,
					pointsEarned: orderData.pointsEarned,
					redeemedRewards: orderData.redeemedRewards,
				})
				console.log('POINTS UPDATED.')
				crashlytics().log('POINTS UPDATED.')
				crashlytics().log(JSON.stringify(response.data))
				console.log(response.data)
			} catch (error) {
				console.log('Error attaching loyalty points: ' + error)
				crashlytics().log('Error attaching loyalty points: ' + error)
			}
		}
		const addRedeemedRewards = async () => {
			try {
				const addRedeemedRewards = httpsCallable(functions, 'addRedeemedRewards')
				console.log('UPDATING REDEEMED REWARDS')
				crashlytics().log('UPDATING REDEEMED REWARDS')
				const response = await addRedeemedRewards({
					orgId: global.org,
					uid: orderData.userId,
					orderId: orderId,
					redeemedRewards: orderData.redeemedRewards,
				})
				console.log('REWARDS UPDATED.')
				crashlytics().log('REWARDS UPDATED.')
				crashlytics().log(JSON.stringify(response.data))

				console.log(response.data)
			} catch (error) {
				console.log('Error attaching loyalty points: ' + error)
				crashlytics().log('Error attaching loyalty points: ' + error)
			}
		}
		const sendDeliveryOrder = async () => {
			try {
				const sendOrderToDoorDash = httpsCallable(functions, 'v2_createDoorDashDeliveryOrder')
				console.log('CREATING DOORDASH ORDER')
				crashlytics().log(
					'CREATING DOORDASH ORDER FOR ' + orderId + ' AND ORDER NUM ' + orderNumber
				)
				console.log('USER ID ' + orderData.userId)
				const mappedItems = orderData.items.map(item => ({
					name: item.name,
					quantity: item.qty,
					external_id: item.id,
					price: item.ppu,
				}))
				//if username or userphone are '', look up userId in database and get the name and phone number
				const doorDashData = {
					pickupTime: cartPrep.isScheduled
						? sub(new Date(cartPrep.deliveryTime), {
								minutes: cartPrep.deliveryPrepTime - cartPrep.longestPrepTime,
							})
						: add(new Date(), parsePrepTime(cartPrep.prepTimeDuration)),
					orgId: global.org,
					userName: orderData.takeOutName,
					userPhone: orderData.selectedMethods.phoneString,
					userId: orderData.userId,
					tipAmount: Math.round(orderData.prices.tip * 100),
					orderTotal: Math.round(orderData.prices.subTotal * 100),
					dropoffAddress: userLocation ? userLocation.longAddress : '',
					items: mappedItems,
					orderNumber: orderNumber,
					orderId: orderId,
					ageVerificationRequired: orderData.ageVerificationRequired,
					isContactless: contactlessDropoff,
					dropoffInstructions:
						userLocation && userLocation.instructions ? userLocation.instructions : '',
					ifUndeliverable: orderData.ageVerificationRequired
						? 'return_to_pickup'
						: delivery.actionIfUndeliverable,
					pickupInstructions: delivery.pickupInstructions,
					textUpdates: selectedMethods.textUpdates,
					organizationName: loadedData[global.org].name || '',
					isSandbox: delivery.isSandbox || isTestMode,
				}
				crashlytics().log(JSON.stringify(doorDashData))
				const response = await sendOrderToDoorDash(doorDashData)
				console.log('DOORDASH ORDER SENT.')
				crashlytics().log('DOORDASH ORDER SENT')

				console.log(response.data)
			} catch (error) {
				console.log('Error sending doordash order: ' + error)
				crashlytics().log('Error sending doordash order: ' + error)
				//TO DO: update status to failed
			}
		}
		const updateModalTimestamps = async (timestamps, orderId) => {
			try {
				const confirmedScreenTime = new Date()?.toISOString?.()
				console.log('UPDATING MODAL TIMESTAMPS')
				crashlytics().log('UPDATING MODAL TIMESTAMPS')

				await cloudFunction('v2_updateModalTimestamps', {
					orgId: global.org,
					orderId: orderId,
					timestamps: timestamps,
					confirmedScreenTimestamp: confirmedScreenTime,
				})
				console.log('MODAL TIMESTAMPS UPDATED.')
				crashlytics().log('MODAL TIMESTAMPS UPDATED.')
			} catch (error) {
				console.error('Error calling cloud function', error)
				crashlytics().log('Error sending calling cloud function: ' + error)
			}
		}

		if (hasRun.current) {
			return
		} else {
			hasRun.current = true
			const isProductOrder = products.length > 0

			if (!isProductOrder && isDelivery) {
				sendDeliveryOrder()
			}
			if (orderData.pointsEarned > 0) {
				addLoyaltyPointsToUser()
			}

			if (orderData.redeemedRewards?.length > 0) {
				addRedeemedRewards()
			}

			if (selectedMethods.email) {
				sendEmailReceipt()
			}
			if (selectedMethods.textMessage && !selectedMethods.textUpdates) {
				sendTextReceipt()
			}
			crashlytics().log('CONFIRM ORDER TEXT: ' + orderText)
			updateModalTimestamps(modalTimestamps, orderId)
			logCompleteCheckoutAnalytics(prices.total / 100, orderId, analyticsItems, prices.taxes / 100)
			dispatch(setDataState({ showKioskStartScreen: true }))
			dispatch(
				setCheckoutState({
					awaitingTerminalInput: false,
					giftCard: null,
					posExtraData: {
						name: '',
						firebaseId: '',
						note: '',
						type: '',
					},
				})
			)
			Platform.OS === 'web' ? emptyCart() : null
		}
	}, [orderId])

	const headerText = global.pos ? 'ORDER PLACED!' : 'THANK YOU!'
	return (
		<View
			style={[
				styles.container,
				{ marginTop: global.kiosk ? '6%' : '10%', marginBottom: global.kiosk ? '8%' : '10%' },
			]}
		>
			<StarPrintReceipt
				item={orderData}
				prices={prices}
				printer={receiptPrinter}
				automaticPrint={selectedMethods.print}
				hidePrintButton
				isOrderConfirmedModal={true}
			/>
			<Text
				style={[
					styles.mainHeader,
					{ color: global.orgColor },
					{ marginBottom: global.kiosk ? '4%' : 0 },
				]}
			>
				{headerText}
			</Text>
			<View style={styles.contentContainer}>
				<View style={styles.rowContainer}>
					<Ionicons name="md-checkbox-outline" size={titleTextSize * 1.5} color="#818181" />
					<Text style={[styles.orderConfirmationText, { marginLeft: 35 }]}>
						{`${descriptor} ORDER`}
						<Text style={{ color: global.orgColor }}>{` #${orderNumber} `}</Text>
						{`HAS BEEN PLACED. `}
						<Text style={{ color: global.orgColor }}>{pickupTextData.toUpperCase()}</Text>
						{selectedPickupPoint?.id === 'store_pickup' && !isDelivery ? ' FROM THE STORE' : ''}
						{!selectedPickupPoint.name || selectedPickupPoint?.id === 'store_pickup' || isDelivery
							? '.'
							: ` AT ${selectedPickupPoint?.name?.toUpperCase()}.`}
					</Text>
				</View>
				{selectedMethods.textMessage &&
					selectedMethods.textUpdates &&
					selectedMethods.phoneString !== '' && (
						<View style={[styles.rowContainer, { marginLeft: '-2.5%' }]}>
							<EvilIcons name="bell" size={titleTextSize * 1.95} color="#818181" />
							<Text style={[styles.orderConfirmationText, { marginLeft: 20 }]}>
								{deliveryEnabled && isDelivery
									? `YOU WILL RECEIVE TEXT UPDATES WITH YOUR DELIVERY PROGRESS`
									: `${pronoun} WILL RECEIVE A TEXT WHEN ${descriptor} ORDER IS READY FOR PICKUP.`}
							</Text>
						</View>
					)}

				<View
					style={[
						styles.rowContainer,
						{ marginLeft: -3, display: orderText !== '' ? 'flex' : 'none' },
					]}
				>
					<Ionicons name="md-receipt-outline" size={titleTextSize * 1.5} color="#818181" />
					<Text style={[styles.orderConfirmationText, { marginLeft: 30 }]}>{orderText}</Text>
				</View>
				{(showLoyalty || posLoyaltyPointsAccrued > 0) && (
					<View style={styles.rowContainer}>
						<AntDesign name="staro" size={titleTextSize * 1.5} color="#818181" />
						<Text style={[styles.orderConfirmationText, { marginLeft: 35 }]}>
							{`YOU HAVE EARNED${
								posLoyaltyPointsAccrued > 0 ? ' ' + posLoyaltyPointsAccrued : ''
							} POINT${posLoyaltyPointsAccrued === 1 ? '' : 'S'}`}
						</Text>
					</View>
				)}
			</View>
			<View style={styles.buttonContainer}>
				{global.kiosk && (
					<TouchableOpacity style={[styles.doneButton, { marginBottom: 30 }]} onPress={handleDone}>
						<Text style={styles.buttonText}>{'DONE'}</Text>
					</TouchableOpacity>
				)}
				<TouchableOpacity
					style={[styles.doneButton, { backgroundColor: global.orgColor, marginBottom: 20 }]}
					onPress={handleDone}
				>
					<Text style={styles.buttonText}>
						{global.kiosk ? 'START NEW ORDER' : 'RETURN TO HOME'}
					</Text>
				</TouchableOpacity>
			</View>
		</View>
	)
}

const styles = StyleSheet.create({
	container: {
		flex: 1,
		alignItems: 'center',
		marginHorizontal: '10%',
		marginVertical: '10%',
		backgroundColor: '#fff',
		marginBottom: 30,
		justifyContent: 'space-between',
		maxHeight: Dimensions.get('window').height * 0.8,
	},
	contentContainer: {
		alignItems: 'flex-start',
		justifyContent: 'center',
		flex: 1,
	},
	rowContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'flex-start',
		marginVertical: '8%',
	},
	orderConfirmationText: {
		marginLeft: '5%',
		fontSize: largeTextSize,
		fontWeight: '600',
		color: '#4E4E4E',
	},
	mainHeader: {
		fontSize: titleTextSize,
		textAlign: 'center',
		fontWeight: '600',
	},
	buttonContainer: {
		alignItems: 'center',
		width: '100%',
	},
	doneButton: {
		backgroundColor: 'grey',
		borderRadius: 12,
		paddingVertical: 20,
		width: '100%',
		alignItems: 'center',
		justifyContent: 'center',
	},
	buttonText: {
		color: Colors.custom.restaurantMainText,
		fontSize: largeTextSize,
		fontWeight: 'bold',
	},
})

export default OrderConfirmed
